import {
  BarChartOutlined,
  EnvironmentOutlined,
  FunctionOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  TagsOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  getAvailableAgeGroups,
  getAvailableCategories,
  getAvailableLocations,
  getClusteringResults,
  getClusteringStatistics,
  getProductsByAgeGroup,
  getProductsByCategory,
  getProductsByLocation,
  getProductsByClusterId,
  performProductClustering,
} from '@apis/product';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { FilterSelect } from '@components/common/FilterSelect';
import React, { useState } from 'react';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface ClusteringData {
  clustering_id: string;
  created_at: string;
  total_products: number;
  n_clusters: number;
  silhouette_score: number;
  clusters: Array<{
    cluster_id: number;
    size: number;
    category_distribution: Record<string, number>;
    location_distribution: Record<string, number>;
    age_group_distribution: Record<string, number>;
    buyer_region_distribution: Record<string, number>;
    avg_fee: number;
    sample_titles: string[];
  }>;
}

interface ProductData {
  _id: string;
  title: string;
  categories: string[];
  fee: number;
  ageMin: number;
  ageMax: number;
  activityType: string;
  tags: string[];
}

const ProductClusteringPage: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<{
    type: 'category' | 'location' | 'age_groups';
    value: string;
  } | null>(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<ProductData[]>([]);
  const [isClusterProductsModalVisible, setIsClusterProductsModalVisible] = useState(false);
  const [selectedClusterProducts, setSelectedClusterProducts] = useState<ProductData[]>([]);

  const queryClient = useQueryClient();

  // 클러스터링 수행 mutation
  const performClusteringMutation = useMutation({
    mutationFn: (forceRecluster: boolean) => performProductClustering(forceRecluster),
    onSuccess: () => {
      message.success('상품 클러스터링이 완료되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['clustering'] });
    },
    onError: (error: any) => {
      message.error(`클러스터링 실패: ${error.response?.data?.message || error.message}`);
    },
  });

  // 클러스터링 결과 조회
  const {
    data: clusteringResults,
    isLoading: isLoadingResults,
    error: resultsError,
  } = useQuery({
    queryKey: ['clustering', 'results'],
    queryFn: () => getClusteringResults(),
    enabled: true,
  });

  // 클러스터링 통계 조회
  const { data: clusteringStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['clustering', 'statistics'],
    queryFn: () => getClusteringStatistics(),
    enabled: true,
  });

  // 사용 가능한 옵션들 조회
  const { data: availableCategories } = useQuery({
    queryKey: ['clustering', 'categories'],
    queryFn: () => getAvailableCategories(),
  });

  const { data: availableLocations } = useQuery({
    queryKey: ['clustering', 'locations'],
    queryFn: () => getAvailableLocations(),
  });

  const { data: availableAgeGroups } = useQuery({
    queryKey: ['clustering', 'age-groups'],
    queryFn: () => getAvailableAgeGroups(),
  });

  // 필터링된 상품 조회
  const { data: filteredProducts, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['clustering', 'products', selectedFilter],
    queryFn: () => {
      if (!selectedFilter) return null;

      // 전체 상품 조회인 경우
      if (selectedFilter.type === 'category' && selectedFilter.value === 'all') {
        // 전체 상품을 가져오기 위해 첫 번째 카테고리로 조회하거나 별도 API 호출
        return getProductsByCategory(''); // 빈 문자열로 전체 조회
      }

      switch (selectedFilter.type) {
        case 'category':
          return getProductsByCategory(selectedFilter.value);
        case 'location':
          return getProductsByLocation(selectedFilter.value);
        case 'age_groups':
          return getProductsByAgeGroup(selectedFilter.value);
        default:
          return null;
      }
    },
    enabled: !!selectedFilter,
  });

  const clusteringData = clusteringResults?.data?.data as ClusteringData | undefined;
  const statsData = clusteringStats?.data?.data;

  // 클러스터 테이블 컬럼 정의
  const clusterColumns = [
    {
      title: '클러스터 ID',
      dataIndex: 'cluster_id',
      key: 'cluster_id',
      render: (id: number) => <Badge count={id} style={{ backgroundColor: '#52c41a' }} showZero />,
    },
    {
      title: '상품 수',
      dataIndex: 'size',
      key: 'size',
      render: (size: number) => <Statistic value={size} />,
    },
    {
      title: '평균 가격',
      dataIndex: 'avg_fee',
      key: 'avg_fee',
      render: (fee: number) => `${fee.toLocaleString()}원`,
    },
    {
      title: '주요 카테고리',
      dataIndex: 'category_distribution',
      key: 'category_distribution',
      render: (categories: Record<string, number>) => {
        const sorted = Object.entries(categories).sort((a, b) => b[1] - a[1]);
        return (
          <Space wrap>
            {sorted?.slice(0, 3).map(([category, count]) => (
              <Tag key={category} color="blue">
                {category} ({count})
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '주요 지역',
      dataIndex: 'location_distribution',
      key: 'location_distribution',
      render: (locations: Record<string, number>) => {
        const sorted = Object.entries(locations).sort((a, b) => b[1] - a[1]);
        return (
          <Space wrap>
            {sorted?.slice(0, 2).map(([location, count]) => (
              <Tag key={location} color="green">
                {location} ({count})
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '주요 연령대',
      dataIndex: 'age_group_distribution',
      key: 'age_group_distribution',
      render: (ageGroups: Record<string, number>) => {
        const sorted = Object.entries(ageGroups).sort((a, b) => b[1] - a[1]);
        return (
          <Space wrap>
            {sorted?.slice(0, 2).map(([ageGroup, count]) => (
              <Tag key={ageGroup} color="orange">
                {ageGroup} ({count})
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '샘플 상품',
      dataIndex: 'sample_titles',
      key: 'sample_titles',
      render: (titles: string[]) => (
        <Tooltip title={titles.join(', ')}>
          <Text ellipsis>{titles[0]}</Text>
        </Tooltip>
      ),
    },
  ];

  // 상품 테이블 컬럼 정의
  const productColumns = [
    {
      title: '상품명',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => (
        <Text ellipsis style={{ maxWidth: 200 }}>
          {title}
        </Text>
      ),
    },
    {
      title: '카테고리',
      dataIndex: 'categories',
      key: 'categories',
      render: (categories: string[]) => (
        <Space wrap>
          {categories?.slice(0, 2).map((category) => (
            <Tag key={category} color="blue">
              {category}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '가격',
      dataIndex: 'fee',
      key: 'fee',
      render: (fee: number) => `${fee?.toLocaleString() || ''}원`,
    },
    {
      title: '연령대',
      key: 'age',
      render: (record: ProductData) => `${record.ageMin}-${record.ageMax}세`,
    },
    {
      title: '활동 유형',
      dataIndex: 'activityType',
      key: 'activityType',
      render: (type: string) => <Tag color="purple">{type}</Tag>,
    },
    {
      title: '태그',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[]) => (
        <Space wrap>
          {tags?.slice(0, 3).map((tag) => (
            <Tag key={tag} color="cyan">
              {tag}
            </Tag>
          ))}
        </Space>
      ),
    },
  ];

  const handlePerformClustering = (forceRecluster = false) => {
    performClusteringMutation.mutate(forceRecluster);
  };

  const handleFilterChange = (type: 'category' | 'location' | 'age_groups', value: string) => {
    setSelectedFilter({ type, value });
  };

  const handleClusterClick = async (clusterId: number) => {
    const response = await getProductsByClusterId(clusterId);
    if (response.data?.data) {
      setSelectedClusterProducts(response.data.data);
      setIsClusterProductsModalVisible(true);
    }
  };

  const handleShowProductDetails = () => {
    // 필터가 선택되었는지 확인
    if (!selectedFilter) {
      message.warning('필터를 먼저 선택해주세요.');
      return;
    }

    // 로딩 중이면 기다리도록 메시지 표시
    if (isLoadingProducts) {
      message.info('상품 데이터를 불러오는 중입니다...');
      return;
    }

    // 데이터 구조를 더 유연하게 처리
    const products = filteredProducts?.data?.data?.products;
    if (products && products.length > 0) {
      setSelectedProducts(products);
      setIsDetailModalVisible(true);
    } else {
      message.info('해당 조건에 맞는 상품이 없습니다.');
      setSelectedProducts([]);
      setIsDetailModalVisible(true);
    }
  };

  if (resultsError) {
    return (
      <Card>
        <Alert
          message="클러스터링 결과를 불러올 수 없습니다"
          description="클러스터링을 먼저 수행해주세요."
          type="warning"
          action={
            <Button type="primary" onClick={() => handlePerformClustering(false)}>
              클러스터링 수행
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <FunctionOutlined /> 상품 클러스터링 관리
      </Title>
      <Paragraph>
        상품을 카테고리, 지역, 구매자 지역, 연령대 기준으로 자동 클러스터링하여 상품 분포와 특성을 분석합니다.
      </Paragraph>

      {/* 컨트롤 영역 */}
      <Card style={{ marginBottom: 24 }}>
        <Space>
          <Button
            type="primary"
            icon={<FunctionOutlined />}
            onClick={() => handlePerformClustering(false)}
            loading={performClusteringMutation.isPending}>
            클러스터링 수행
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => handlePerformClustering(true)}
            loading={performClusteringMutation.isPending}>
            강제 재분석
          </Button>
          <Tooltip title="클러스터링 품질을 나타내는 실루엣 점수입니다. 높을수록 좋습니다.">
            <Button icon={<InfoCircleOutlined />} type="link">
              도움말
            </Button>
          </Tooltip>
        </Space>
      </Card>

      {/* 통계 영역 */}
      {statsData && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic title="총 상품 수" value={statsData.total_products} prefix={<ShoppingOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="클러스터 수" value={statsData.n_clusters} prefix={<FunctionOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="실루엣 점수"
                value={statsData.silhouette_score}
                precision={3}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="생성 시간"
                value={new Date(statsData.created_at).toLocaleString()}
                prefix={<InfoCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 필터 영역 */}
      <Card title="상품 필터링" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>
                <TagsOutlined /> 카테고리별 조회
              </Text>
              <FilterSelect
                style={{ width: '100%' }}
                placeholder="카테고리 선택"
                onChange={(value) => handleFilterChange('category', value)}
                allowClear>
                {availableCategories?.data?.data?.categories?.map((category: string) => (
                  <Option key={category} value={category}>
                    {category} ({availableCategories.data.data.category_counts[category]})
                  </Option>
                ))}
              </FilterSelect>
            </Space>
          </Col>
          <Col span={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>
                <EnvironmentOutlined /> 구매자 지역별 조회
              </Text>
              <FilterSelect
                style={{ width: '100%' }}
                placeholder="지역 선택"
                onChange={(value) => handleFilterChange('location', value)}
                allowClear>
                {availableLocations?.data?.data?.locations?.map((location: string) => (
                  <Option key={location} value={location}>
                    {location} ({availableLocations.data.data.location_counts[location]})
                  </Option>
                ))}
              </FilterSelect>
            </Space>
          </Col>
          <Col span={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>
                <TeamOutlined /> 연령대별 조회
              </Text>
              <FilterSelect
                style={{ width: '100%' }}
                placeholder="연령대 선택"
                onChange={(value) => handleFilterChange('age_groups', value)}
                allowClear>
                {availableAgeGroups?.data?.data?.age_groups?.map((ageGroup: string) => (
                  <Option key={ageGroup} value={ageGroup}>
                    {ageGroup} ({availableAgeGroups.data.data.age_group_counts[ageGroup]})
                  </Option>
                ))}
              </FilterSelect>
            </Space>
          </Col>
        </Row>

        {selectedFilter && (
          <div style={{ marginTop: 16 }}>
            <Space>
              <Text>
                선택된 필터: <Tag color="blue">{selectedFilter.value}</Tag>
              </Text>
              {(() => {
                // 다양한 데이터 구조에서 total_count 추출
                const totalCount = filteredProducts?.data?.data?.total_count;
                return totalCount ? <Text>총 {totalCount}개 상품</Text> : null;
              })()}
              <Button size="small" onClick={handleShowProductDetails} loading={isLoadingProducts} type="primary">
                상품 목록 보기
                {(() => {
                  const totalCount = filteredProducts?.data?.data?.total_count;
                  return totalCount ? `(${totalCount}개)` : '';
                })()}
              </Button>
            </Space>
          </div>
        )}
      </Card>

      {/* 클러스터 정보 */}
      {clusteringData && (
        <Card title="클러스터 분석 결과" loading={isLoadingResults}>
          <Table
            dataSource={clusteringData.clusters}
            columns={clusterColumns}
            rowKey="cluster_id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1200 }}
            onRow={(record) => ({
              onClick: () => handleClusterClick(record.cluster_id),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      )}

      {/* 상품 세부 정보 모달 */}
      <Modal
        title={`${selectedFilter?.value === 'all' ? '전체 상품' : selectedFilter?.value} - 상품 목록`}
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={null}
        width={1000}>
        {isLoadingProducts ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>상품 데이터 로딩 중...</div>
          </div>
        ) : (
          <Table
            dataSource={selectedProducts}
            columns={productColumns}
            rowKey="_id"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`,
              pageSizeOptions: ['5', '10', '20', '50'],
            }}
            size="small"
          />
        )}
      </Modal>

      {/* 클러스터 상품 목록 모달 */}
      <Modal
        title={`클러스터 ${selectedClusterProducts?.length} - 상품 목록 (${selectedClusterProducts?.length}개)`}
        open={isClusterProductsModalVisible}
        onCancel={() => setIsClusterProductsModalVisible(false)}
        footer={null}
        width={1200}>
        <Table
          dataSource={selectedClusterProducts}
          columns={productColumns}
          rowKey="_id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`,
            pageSizeOptions: ['5', '10', '20', '50'],
          }}
          size="small"
        />
      </Modal>

      {/* 로딩 상태 */}
      {(isLoadingResults || isLoadingStats || performClusteringMutation.isPending) && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            {performClusteringMutation.isPending ? '클러스터링 수행 중...' : '데이터 로딩 중...'}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductClusteringPage;
